import React from "react";

const Eula = () => {
  return (
    <div className="page">
      <h1>GH Tracker – End-User License Agreement (EULA)</h1>

      <p>
        <strong>Effective date:</strong> {new Date().toLocaleDateString()}
      </p>
      <p>
        <strong>Developer/Company:</strong> RedCalf
      </p>
      <p>
        <strong>Contact:</strong> support@GH Tracker.com
      </p>

      <h2>1. Agreement to Terms</h2>
      <p>
        By downloading, installing, or using the GH Tracker mobile application
        ("App"), you agree to be bound by this End-User License Agreement
        ("Agreement"). If you do not agree to the terms of this Agreement, do
        not install or use the App.
      </p>

      <h2>2. License Grant</h2>
      <p>
        RedCalf grants you a limited, non-exclusive, non-transferable, revocable
        license to download, install, and use the App on a mobile device that
        you own or control, solely for your personal, non-commercial purposes,
        subject to the terms and conditions of this Agreement.
      </p>

      <h3>License Restrictions</h3>
      <p>You agree not to:</p>
      <ul>
        <li>
          Copy, modify, or create derivative works based on the App or any part
          thereof;
        </li>
        <li>
          Distribute, transfer, sublicense, lease, lend, or rent the App to any
          third party;
        </li>
        <li>
          Reverse engineer, decompile, disassemble, or attempt to derive the
          source code of the App;
        </li>
        <li>
          Remove, alter, or obscure any proprietary notices (including copyright
          and trademark notices) on the App;
        </li>
        <li>
          Use the App for any commercial purpose or for the benefit of any third
          party;
        </li>
        <li>
          Use the App in any manner that violates any applicable law or
          regulation;
        </li>
        <li>
          Use the App to transmit any harmful code, viruses, or malicious
          software.
        </li>
      </ul>

      <h2>3. Ownership</h2>
      <p>
        The App, including all intellectual property rights therein, is and will
        remain the sole and exclusive property of RedCalf. All rights not
        expressly granted to you under this Agreement are reserved by RedCalf.
      </p>

      <h2>4. User Data and Privacy</h2>
      <p>
        Your use of the App is subject to our Privacy Policy, which is
        incorporated into this Agreement by reference. Please review our Privacy
        Policy to understand how we collect, use, and protect your information.
      </p>
      <p>
        <strong>Key Points:</strong>
      </p>
      <ul>
        <li>
          Data you enter (age, height, weight, dosage) is collected for app
          functionality and analytics.
        </li>
        <li>
          A random, resettable device identifier is used to link your entries
          over time.
        </li>
        <li>
          Photos you attach are stored locally on your device and are not
          uploaded to our servers.
        </li>
        <li>
          You may reset your data and request deletion at any time through the
          app or by contacting us.
        </li>
      </ul>

      <h2>5. Medical Disclaimer</h2>
      <div
        style={{
          padding: "1.5rem",
          background: "#fff3cd",
          border: "2px solid #ffc107",
          borderRadius: "6px",
          marginBottom: "1.5rem",
        }}
      >
        <p>
          <strong>IMPORTANT MEDICAL DISCLAIMER:</strong>
        </p>
        <p>
          GH Tracker is provided for informational and record-keeping purposes
          only and is <strong>NOT A MEDICAL DEVICE</strong>. The App is not
          intended to diagnose, treat, cure, or prevent any disease or medical
          condition.
        </p>
        <p>
          Percentages, percentiles, growth predictions, and other metrics
          displayed in the App may involve rounding, estimation, or
          approximation and may not be exact. These metrics are provided for
          informational purposes only.
        </p>
        <p>
          <strong>
            Always consult a qualified healthcare professional for medical
            advice, diagnoses, and treatment decisions.
          </strong>{" "}
          Never disregard professional medical advice or delay seeking medical
          treatment because of information you have read or seen in the App.
        </p>
        <p>
          If you have a medical emergency, call emergency services immediately.
        </p>
      </div>

      <h2>6. Disclaimer of Warranties</h2>
      <p>
        THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY
        KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY. TO THE MAXIMUM EXTENT
        PERMITTED BY APPLICABLE LAW, REDCALF DISCLAIMS ALL WARRANTIES, INCLUDING
        BUT NOT LIMITED TO:
      </p>
      <ul>
        <li>
          Implied warranties of merchantability, fitness for a particular
          purpose, title, and non-infringement;
        </li>
        <li>
          Warranties that the App will be uninterrupted, error-free, secure, or
          free of viruses or other harmful components;
        </li>
        <li>
          Warranties regarding the accuracy, reliability, or completeness of any
          information, content, or calculations provided by the App.
        </li>
      </ul>
      <p>
        Some jurisdictions do not allow the exclusion of implied warranties, so
        the above exclusions may not apply to you.
      </p>

      <h2>7. Limitation of Liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
        REDCALF, ITS AFFILIATES, DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR
        LICENSORS BE LIABLE FOR ANY:
      </p>
      <ul>
        <li>
          Indirect, incidental, special, consequential, or exemplary damages;
        </li>
        <li>
          Loss of profits, revenue, data, goodwill, or other intangible losses;
        </li>
        <li>Damages arising from your use or inability to use the App;</li>
        <li>
          Damages resulting from any unauthorized access to or use of our
          servers and/or any personal information stored therein;
        </li>
        <li>
          Damages resulting from any errors, mistakes, or inaccuracies in the
          App or its content;
        </li>
        <li>Personal injury or property damage of any nature whatsoever;</li>
      </ul>
      <p>
        EVEN IF REDCALF HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>
      <p>
        IN NO EVENT SHALL REDCALF'S TOTAL LIABILITY TO YOU FOR ALL DAMAGES,
        LOSSES, AND CAUSES OF ACTION EXCEED THE AMOUNT YOU PAID TO REDCALF FOR
        THE APP IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM (OR, IF NO PAYMENT
        WAS MADE, ONE HUNDRED U.S. DOLLARS ($100)).
      </p>
      <p>
        Some jurisdictions do not allow the limitation or exclusion of liability
        for incidental or consequential damages, so the above limitations may
        not apply to you.
      </p>

      <h2>8. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless RedCalf, its
        affiliates, and their respective directors, officers, employees, and
        agents from and against any and all claims, damages, obligations,
        losses, liabilities, costs, or expenses (including reasonable attorneys'
        fees) arising from:
      </p>
      <ul>
        <li>Your use of or inability to use the App;</li>
        <li>Your violation of this Agreement;</li>
        <li>Your violation of any third-party rights;</li>
        <li>
          Any medical decisions you make based on information from the App.
        </li>
      </ul>

      <h2>9. Updates and Modifications</h2>
      <p>
        RedCalf may from time to time develop and provide updates to the App,
        which may include upgrades, bug fixes, patches, and other error
        corrections and/or new features (collectively, "Updates"). Updates may
        also modify or delete in their entirety certain features and
        functionality. You agree that RedCalf has no obligation to provide any
        Updates or to continue to provide or enable any particular features or
        functionality.
      </p>
      <p>
        Based on your device settings, when your device is connected to the
        internet:
      </p>
      <ul>
        <li>The App may automatically download and install Updates; or</li>
        <li>
          You may receive notice of or be prompted to download and install
          available Updates.
        </li>
      </ul>
      <p>
        You acknowledge that you may be required to install Updates to use the
        App, and you agree to promptly install any Updates we provide. Your
        continued use of the App following any Updates constitutes your
        acceptance of such Updates.
      </p>

      <h2>10. Term and Termination</h2>
      <p>
        This Agreement is effective from the date you first download, install,
        or use the App and continues until terminated by you or RedCalf.
      </p>

      <h3>Termination by You</h3>
      <p>
        You may terminate this Agreement at any time by uninstalling the App
        from your device and ceasing all use of the App.
      </p>

      <h3>Termination by RedCalf</h3>
      <p>
        RedCalf may terminate this Agreement and your license to use the App at
        any time, with or without notice, if you breach any term of this
        Agreement. Upon termination:
      </p>
      <ul>
        <li>Your license to use the App will immediately cease;</li>
        <li>You must immediately stop using the App and uninstall it;</li>
        <li>
          Sections 3 (Ownership), 5 (Medical Disclaimer), 6 (Disclaimer of
          Warranties), 7 (Limitation of Liability), 8 (Indemnification), and 13
          (Governing Law and Dispute Resolution) will survive termination.
        </li>
      </ul>

      <h2>11. Third-Party Services and Content</h2>
      <p>
        The App may contain links to third-party websites, services, or
        resources. RedCalf is not responsible or liable for:
      </p>
      <ul>
        <li>
          The availability or accuracy of such websites, services, or resources;
        </li>
        <li>
          The content, products, or services available from such websites,
          services, or resources.
        </li>
      </ul>
      <p>
        Your use of third-party services is at your own risk and subject to the
        terms and conditions of those third parties.
      </p>

      <h2>12. Export Compliance</h2>
      <p>
        You may not use, export, or re-export the App except as authorized by
        United States law and the laws of the jurisdiction in which the App was
        obtained. You represent and warrant that you are not:
      </p>
      <ul>
        <li>
          Located in a country that is subject to a U.S. Government embargo or
          that has been designated by the U.S. Government as a "terrorist
          supporting" country;
        </li>
        <li>
          Listed on any U.S. Government list of prohibited or restricted
          parties.
        </li>
      </ul>

      <h2>13. Governing Law and Dispute Resolution</h2>
      <p>
        This Agreement shall be governed by and construed in accordance with the
        laws of the State of [Your State], United States of America, without
        regard to its conflict of law provisions.
      </p>
      <p>
        Any dispute, claim, or controversy arising out of or relating to this
        Agreement or the breach, termination, enforcement, interpretation, or
        validity thereof shall be resolved through binding arbitration in
        accordance with the rules of the American Arbitration Association. The
        arbitration shall take place in [Your City, State]. Judgment on the
        award rendered by the arbitrator(s) may be entered in any court having
        jurisdiction thereof.
      </p>
      <p>
        Notwithstanding the foregoing, either party may seek injunctive or other
        equitable relief in any court of competent jurisdiction.
      </p>

      <h2>14. Severability</h2>
      <p>
        If any provision of this Agreement is found to be invalid, illegal, or
        unenforceable, the remaining provisions shall continue in full force and
        effect. The invalid, illegal, or unenforceable provision shall be deemed
        modified to the minimum extent necessary to make it valid, legal, and
        enforceable.
      </p>

      <h2>15. Entire Agreement</h2>
      <p>
        This Agreement, together with our Privacy Policy, constitutes the entire
        agreement between you and RedCalf regarding the App and supersedes all
        prior or contemporaneous understandings and agreements, whether written
        or oral, regarding the App.
      </p>

      <h2>16. Changes to This Agreement</h2>
      <p>
        RedCalf reserves the right to modify this Agreement at any time. We will
        notify you of material changes by:
      </p>
      <ul>
        <li>Posting the updated Agreement in the App;</li>
        <li>Updating the "Effective date" at the top of this Agreement;</li>
        <li>
          Providing in-app notification or requiring you to accept the updated
          Agreement before continuing to use the App.
        </li>
      </ul>
      <p>
        Your continued use of the App after any such changes constitutes your
        acceptance of the new Agreement. If you do not agree to the updated
        Agreement, you must stop using the App and uninstall it.
      </p>

      <h2>17. Contact Information</h2>
      <p>If you have any questions about this Agreement, please contact us:</p>
      <p>
        <strong>RedCalf</strong>
      </p>
      <p>
        <strong>Email:</strong> support@GH Tracker.com
      </p>
      <p>
        <strong>Address:</strong> [Your Company Address]
      </p>

      <div
        style={{
          marginTop: "2rem",
          padding: "1rem",
          background: "#f6f8fa",
          borderRadius: "6px",
          border: "1px solid #e1e4e8",
        }}
      >
        <p>
          <strong>Acknowledgment:</strong> By using GH Tracker, you acknowledge
          that you have read, understood, and agree to be bound by this End-User
          License Agreement. If you do not accept these terms, you must not use
          the App.
        </p>
      </div>
    </div>
  );
};

export default Eula;
